import { isBefore } from 'date-fns';
import { Nullable } from '../../@types/common';
import { MemberDetails, MemberDetailsFormData, Address, Email, Phone, RelationshipSubType } from '../../@types/Members';
import * as _ from 'lodash';
import { dateFormatter } from './fieldsFormatters';

export const transformIncomingMemberDetails: (member: MemberDetails) => MemberDetailsFormData = (
  member: MemberDetails
): MemberDetailsFormData => {
  const {
    firstNm,
    lastNm,
    middleNm,
    groupId,
    group,
    createdAt,
    relatedInstances,
    dob,
    genderCd,
    languagePreferences,
    sourceMrnId,
    effectiveStartDt,
    effectiveEndDt,
    phones,
    primaryFlg,
    readOnly,
    primaryId
  } = member;
  const homeAddress: Nullable<Address> = getAddressOfType(member, 'ADDRESSTYPE_HOME');
  const mailingAddress: Nullable<Address> = getAddressOfType(member, 'ADDRESSTYPE_MAIL');
  const contactEmail: Nullable<Email> = getEmailOfType(member, 'EMAILTYPE_WORK');
  const [phone1, phone2] = phones ? phones.sort(phoneSortByPreferredFlg) : [];
  const primaryRelation = relatedInstances?.find((instance) => instance.mrnId === primaryId);
  const relationshipSubtypeCd: Record<'relationshipSubtypeCd', RelationshipSubType> | {} =
    primaryFlg === 'N' && primaryRelation?.relationshipSubtypeCd
      ? { relationshipSubtypeCd: primaryRelation.relationshipSubtypeCd }
      : {};
  return {
    firstNm,
    lastNm,
    groupId,
    group,
    createdAt,
    originMember: member,
    dob: dob ? dateFormatter(dob) : dob,
    relatedInstances,
    genderCd,
    middleNm,
    primaryFlg,
    readOnly,
    languageCd: languagePreferences && languagePreferences[0] ? languagePreferences[0].languageCd : undefined,
    clientId: sourceMrnId,
    effectiveStartDt: effectiveStartDt && !readOnly ? dateFormatter(effectiveStartDt) : effectiveStartDt,
    effectiveEndDt: effectiveEndDt && !readOnly ? dateFormatter(effectiveEndDt) : '',
    homeAddress: homeAddress ? { ...homeAddress, isPreferred: homeAddress.preferredFlg === 'Y' } : undefined,
    mailingAddress: mailingAddress
      ? { ...mailingAddress, isPreferred: mailingAddress.preferredFlg === 'Y' }
      : undefined,
    isMailingAddressDifferent: !compareAddresses(homeAddress, mailingAddress),
    contactEmail: contactEmail ? contactEmail.emailAddress : undefined,
    phoneIsPreferred: phone2 && phone2.preferredFlg === 'Y' ? 'phone2' : 'phone1',
    phone1: phone1 ? { ...phone1, phoneNumber: phone1.areaCode + phone1.phoneNumber } : undefined,
    phone2: phone2 ? { ...phone2, phoneNumber: phone2.areaCode + phone2.phoneNumber } : undefined,
    relatedInstancesPresent: (relatedInstances && relatedInstances.length > 0) || false,
    couldBeReactivated: effectiveEndDt
      ? isBefore(new Date(effectiveEndDt), new Date()) || isBefore(new Date(effectiveEndDt), new Date(effectiveStartDt))
      : false,
    ...relationshipSubtypeCd
  };
};

const compareAddresses: (addr1: Nullable<Address>, addr2: Nullable<Address>) => boolean = (
  addr1: Nullable<Address>,
  addr2: Nullable<Address>
): boolean => {
  if (!addr1 || !addr2) return true;
  const fieldsToOmit = ['preferredFlg', 'addressTypeCd'];
  return _.isEqual(_.omit(addr1, fieldsToOmit), _.omit(addr2, fieldsToOmit));
};

const getAddressOfType: (member: MemberDetails, type: string) => Nullable<Address> = (
  member: MemberDetails,
  type: string
): Nullable<Address> => {
  return member.addresses ? member.addresses.find((addr: Address) => addr.addressTypeCd === type) || null : null;
};

const getEmailOfType: (member: MemberDetails, type: string) => Nullable<Email> = (
  member: MemberDetails,
  type: string
): Nullable<Email> => {
  return member.emails ? member.emails.find((email: Email) => email.emailTypeCd === type) || null : null;
};

type SortResult = 0 | 1 | -1;
const phoneSortByPreferredFlg: (phone1: Phone, phone2: Phone) => SortResult = (
  phone1: Phone,
  phone2: Phone
): SortResult => {
  const phone1Ispreferred = phone1.preferredFlg === 'Y';
  const phone2IsPreferred = phone2.preferredFlg === 'Y';

  if (phone1Ispreferred === phone2IsPreferred) {
    return 0;
  }

  if (phone1Ispreferred) {
    return -1;
  }

  return 1;
};
